/**
 * @file homeRail.tsx
 * @brief The phone's Home rail kit, on the web.
 *
 * @details
 * One recipe shared by every Home rail, as on mobile (RecentMeetings,
 * RecentSessions, RecentNotes, HorizontalScheduledSessions):
 * - **Section heading:** a 24px icon in faded ink, the eyebrow "Recent"
 *   above the heading, and a chevron that collapses the section. A faint
 *   line runs from the heading to the chevron, centred on it, as the
 *   phone's `SectionHeadingRule` (U1, 29-10-2026; was beside "Recent").
 * - **Card head:** title (15 bold, the date's grey) and date (13 semibold)
 *   sit INSIDE the card, over a hairline (`headInside`, DES 07-10-2026, as
 *   the phone's Home). Was 17 bold above the card.
 * - **Card body:** white, 20px radius, soft shadow.
 * - **Tabs:** pills overhanging the card's top-right edge by 10px.
 * - **Rail:** snapping, a "See All" circle at the end, and pagination dots.
 *   Dots are clickable, because a mouse cannot swipe.
 *
 * Numbers are the phone's (`FRONTEND/src/shared/components/Recent*`).
 *
 * - **Loading:** `RailSkeleton`, card-shaped placeholders in the rail's own
 *   sizes, web-only (DES 01-10-2026). The phone's Home shows nothing until
 *   its fetch returns; the web asked for skeletons instead.
 *
 * @author DES
 * @date 30-09-2026
 */

import { Children, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, type LucideIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/shared/theme/cn";

// ----------------------------------------------------------------------
// Section heading — VZSectionHeading tone="strong" + SectionCollapseToggle
// ----------------------------------------------------------------------
export function HomeSection({
  icon: Icon,
  title,
  eyebrow = "Recent",
  collapsed,
  onToggle,
  toggleLabel,
  action,
  rule = true,
  children,
}: {
  icon: LucideIcon;
  /** Heading under the eyebrow, e.g. "Boardroom Meetings". */
  title: ReactNode;
  /** Small word above the heading; null for none (Scheduled). */
  eyebrow?: string | null;
  collapsed: boolean;
  onToggle: () => void;
  toggleLabel: string;
  /** A small control left of the chevron, e.g. the admin's Mine ▾ (U1). */
  action?: ReactNode;
  /** The line from the heading to the chevron (or the action). Off for
   *  Quick Notes, whose own timeline line sits just under the heading
   *  (DES 21-10-2026). */
  rule?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mb-6">
      {/* One vertical centre for icon, heading, line, (action) and chevron
          (U1, DES 29-10-2026; was top-aligned on "Recent" with the line
          beside it). 8px from the line to the action or the chevron's box;
          the action keeps 8px to the chevron's ink (its 36px box has 11px
          of air before the ink), so both sides of it read the same. */}
      <div className="mb-3 flex items-center justify-between px-5">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <Icon className="h-6 w-6 shrink-0 text-app-text/40" strokeWidth={1.5} />
          <span className="flex min-w-0 shrink flex-col">
            {eyebrow ? (
              <span className="-mb-0.5 text-[13px] font-semibold leading-4 text-app-text/40">{eyebrow}</span>
            ) : null}
            {/* White, full strength (DES 07-10-2026), as the phone's
                `tone="full"`; was 60%. The eyebrow and icon stay faded. */}
            <span className="text-lg font-semibold tracking-[-0.5px] text-app-text">{title}</span>
          </span>
          {/* The phone's SectionHeadingRule: textPrimary at 0.14. */}
          <span
            aria-hidden
            className={cn("mr-2 h-px min-w-[14px] flex-1", rule ? "bg-app-text/[0.14]" : "")}
          />
        </button>
        {action ? <div className="-mr-[3px] flex shrink-0 items-center">{action}</div> : null}
        <button
          type="button"
          onClick={onToggle}
          aria-label={`${collapsed ? "Show" : "Hide"} ${toggleLabel}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center"
        >
          <ChevronDown
            className={cn(
              "h-6 w-6 text-app-text/40 transition-transform duration-200",
              collapsed ? "" : "rotate-180",
            )}
            strokeWidth={1.5}
          />
        </button>
      </div>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-in-out",
          collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">{children}</div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------
// Tabs — the pills overhanging a card's top-right edge
// ----------------------------------------------------------------------
export function CardTab({
  icon: Icon,
  children,
  dot,
  tone = "capsule",
  className,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  /** A status tab's dot colour class, e.g. "bg-brand". */
  dot?: string;
  tone?: "capsule" | "card";
  className?: string;
}) {
  return (
    <span
      className={cn(
        // The phone writes each tab's own case: "Ready", "£2.00", "TYPED".
        "inline-flex items-center gap-1 rounded-full px-2.5 py-[3px] text-[11px] font-bold tracking-[0.5px] text-app-text-secondary shadow-[0_1px_2px_rgba(0,0,0,0.1)]",
        tone === "capsule" ? "bg-app-capsule" : "bg-app-card",
        className,
      )}
    >
      {dot ? <span className={cn("h-[7px] w-[7px] rounded-full", dot)} /> : null}
      {Icon ? <Icon className="h-3 w-3" strokeWidth={2} /> : null}
      {children}
    </span>
  );
}

// ----------------------------------------------------------------------
// One rail item: head above, card below, tabs on the edge
// ----------------------------------------------------------------------
export function RailCard({
  width,
  title,
  date,
  dateExtra,
  tabs,
  leftTabs,
  below,
  bodyHeight,
  headInside = false,
  headRight,
  bodyStyle,
  to,
  state,
  onClick,
  className,
  bodyClassName,
  children,
}: {
  width: number;
  title: string;
  date: string;
  /** Title + date inside the card, over a hairline (DES 07-10-2026). */
  headInside?: boolean;
  /** `headInside`: on the title's line, at its end (the duration). */
  headRight?: ReactNode;
  /** The card body's own style, e.g. the sessions' fill colour. */
  bodyStyle?: CSSProperties;
  /** Beside the date, e.g. the sessions' PAID pill (04-10-2026). */
  dateExtra?: ReactNode;
  tabs?: ReactNode;
  /** Tabs on the card's top-LEFT edge (the sessions' REALTIME / MEDICAL). */
  leftTabs?: ReactNode;
  /** Under the card, outside it (the sessions' tags). */
  below?: ReactNode;
  bodyHeight: number;
  to?: string;
  /** Router state passed with `to` (e.g. the opened page's Back). */
  state?: unknown;
  onClick?: () => void;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const dateLine = (
    <>
      <p className="truncate text-[13px] font-semibold text-app-text-secondary">{date}</p>
      {dateExtra}
    </>
  );
  const inner = (
    <>
      {headInside ? null : (
        <div className="mb-2.5 flex h-[46px] flex-col justify-end px-1 pb-1">
          <p className="truncate text-[17px] font-bold text-app-text">{title}</p>
          {dateExtra ? (
            <div className="mt-0.5 flex min-w-0 items-center gap-2">
              <p className="truncate text-[13px] font-semibold text-app-text-secondary">{date}</p>
              {dateExtra}
            </div>
          ) : (
            <p className="mt-0.5 truncate text-[13px] font-semibold text-app-text-secondary">
              {date}
            </p>
          )}
        </div>
      )}
      {/* Inside: 10px above for the tabs that hang off the card's edge. */}
      <div className={cn("relative", headInside && "mt-2.5")}>
        {leftTabs ? (
          <div className="absolute -top-2.5 left-6 z-10 flex gap-1.5">{leftTabs}</div>
        ) : null}
        {tabs ? <div className="absolute -top-2.5 right-4 z-10 flex gap-1.5">{tabs}</div> : null}
        <div
          className={cn(
            // Flat like the Boardrooms cards; the shadow comes on hover, so a
            // card shows it can be opened (DES 01-10-2026).
            "overflow-hidden rounded-[20px] bg-app-card transition-[box-shadow,transform] duration-150",
            (to || onClick) &&
              "group-hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] group-active:scale-[0.97]",
            bodyClassName,
          )}
          style={{ height: bodyHeight, ...bodyStyle }}
        >
          {headInside ? (
            <div className="px-4 pt-4">
              <div className="flex h-[22px] min-w-0 items-center gap-2">
                <p className="min-w-0 flex-1 truncate text-[15px] font-bold text-app-text-secondary">
                  {title}
                </p>
                {headRight}
              </div>
              <div className="mt-0.5 flex h-[18px] min-w-0 items-center gap-2">{dateLine}</div>
              <div className="mt-3 h-px bg-border" />
            </div>
          ) : null}
          {children}
        </div>
      </div>
      {below}
    </>
  );
  const cls = cn(
    "group block shrink-0 snap-start text-left",
    (to || onClick) && "cursor-pointer",
    className,
  );
  if (to) {
    return (
      <Link to={to} state={state} className={cls} style={{ width }}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls} style={{ width }}>
        {inner}
      </button>
    );
  }
  return (
    <div className={cls} style={{ width }}>
      {inner}
    </div>
  );
}

// ----------------------------------------------------------------------
// The rail: snapping row + See All + dots
// ----------------------------------------------------------------------
export function HomeRail({
  cardWidth,
  bodyHeight,
  seeAllTo,
  headInside = false,
  seeAllTop,
  children,
}: {
  cardWidth: number;
  bodyHeight: number;
  seeAllTo?: string;
  /** The cards carry their head inside: See All sits 10px down, not 56. */
  headInside?: boolean;
  /** See All's top offset when the items are not cards (Notes). */
  seeAllTop?: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(0);
  const snap = cardWidth + 12;
  const count = Children.count(children) + (seeAllTo ? 1 : 0);
  // Dots only when the row scrolls: a wide Mac window can show every card,
  // and then the dots would point at nothing (19-10-2026).
  const [overflows, setOverflows] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count]);

  // Drag with the mouse to scroll, as a finger does on the phone (DES
  // 01-10-2026: "desktop per card ko pakar kar scroll nahin kia ja sakta").
  // Touch and trackpads scroll natively and are left alone. Snapping is off
  // while dragging (it would fight every move), then the row settles on the
  // nearest card. A drag never opens the card it ends on; a click still does.
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const justDragged = useRef(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse" || e.button !== 0 || !ref.current) return;
    drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false };
    const move = (ev: PointerEvent) => {
      const d = drag.current;
      const el = ref.current;
      if (!d || !el) return;
      const dx = ev.clientX - d.x;
      if (!d.moved && Math.abs(dx) > 5) {
        d.moved = true;
        setDragging(true);
      }
      if (d.moved) el.scrollLeft = d.left - dx;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = drag.current;
      drag.current = null;
      if (!d?.moved) return;
      justDragged.current = true;
      window.setTimeout(() => (justDragged.current = false), 0);
      const el = ref.current;
      if (el) {
        const target = Math.round(el.scrollLeft / snap) * snap;
        el.scrollTo({ left: target, behavior: "smooth" });
        window.setTimeout(() => setDragging(false), 350);
      } else {
        setDragging(false);
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div>
      <div
        ref={ref}
        onScroll={(e) => setPage(Math.round(e.currentTarget.scrollLeft / snap))}
        onPointerDown={onPointerDown}
        onClickCapture={(e) => {
          if (justDragged.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        // Links and images would otherwise start the browser's own drag.
        onDragStart={(e) => e.preventDefault()}
        className={cn(
          "flex scroll-px-5 gap-3 overflow-x-auto px-5 pb-3 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          dragging ? "cursor-grabbing select-none" : "snap-x snap-mandatory",
        )}
      >
        {children}
        {seeAllTo ? (
          <Link
            to={seeAllTo}
            className="flex w-24 shrink-0 snap-start flex-col items-center justify-center gap-2"
            style={{ marginTop: seeAllTop ?? (headInside ? 10 : 56), height: bodyHeight }}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-app-card shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
              <ArrowRight className="h-6 w-6 text-brand" strokeWidth={1.5} />
            </span>
            <span className="text-sm font-semibold text-brand">See All</span>
          </Link>
        ) : null}
      </div>
      {count > 1 && overflows ? (
        <div className="mt-3 flex justify-center gap-1.5">
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to item ${i + 1}`}
              onClick={() => ref.current?.scrollTo({ left: i * snap, behavior: "smooth" })}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === page ? "w-4 bg-brand" : "w-1.5 bg-app-quaternary-dark",
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------
// Loading placeholder: three cards in the rail's shape, pulsing
// ----------------------------------------------------------------------
/** Grey bar in the phone's skeleton tone; the wrapper pulses. */
function Bone({ className }: { className?: string }) {
  return <Skeleton still className={cn("rounded", className)} />;
}

export function RailSkeleton({
  cardWidth,
  bodyHeight,
  headInside = false,
}: {
  cardWidth: number;
  bodyHeight: number;
  /** As `RailCard headInside`: title, date and hairline inside the card. */
  headInside?: boolean;
}) {
  if (headInside) {
    return (
      <div
        role="status"
        aria-label="Loading"
        className="flex animate-pulse gap-3 overflow-hidden px-5 pb-3 pt-1"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="mt-2.5 shrink-0 rounded-[20px] bg-app-card px-4 pt-4"
            style={{ width: cardWidth, height: bodyHeight }}
          >
            <span className="flex h-[22px] items-center justify-between">
              <Bone className="h-3.5 w-28" />
              <Bone className="h-3 w-10" />
            </span>
            <span className="mt-0.5 flex h-[18px] items-center">
              <Bone className="h-3 w-24" />
            </span>
            <div className="mt-3 h-px bg-border" />
            <div className="mt-3 flex items-center gap-2">
              <Bone className="h-5 w-5 rounded-full" />
              <Bone className="h-0.5 w-3" />
              <Bone className="h-5 w-5 rounded-full" />
              <span className="flex-1" />
              <Bone className="h-4 w-4" />
              <Bone className="h-4 w-4" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-label="Loading"
      className="flex animate-pulse gap-3 overflow-hidden px-5 pb-3 pt-1"
    >
      {[0, 1, 2].map((i) => (
        <div key={i} className="shrink-0" style={{ width: cardWidth }}>
          <div className="mb-2.5 flex h-[46px] flex-col justify-end gap-1.5 px-1 pb-1">
            <Bone className="h-4 w-3/5" />
            <Bone className="h-3 w-2/5" />
          </div>
          <div className="relative">
            <div className="absolute -top-2.5 right-4 flex gap-1.5">
              <Bone className="h-[19px] w-12 rounded-full" />
              <Bone className="h-[19px] w-14 rounded-full" />
            </div>
            <div
              className="flex flex-col justify-center gap-2.5 rounded-[20px] bg-app-card px-3"
              style={{ height: bodyHeight }}
            >
              <div className="flex">
                {[0, 1, 2].map((a) => (
                  <Bone
                    key={a}
                    className={cn(
                      "h-[26px] w-[26px] rounded-full border-2 border-app-card",
                      a > 0 && "-ml-2",
                    )}
                  />
                ))}
              </div>
              <Bone className="h-3 w-4/5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The Notes rail's placeholder: the timeline line with three nodes, and
 *  each note's title, date, kind and preview as bars (`NoteTimelineItem`). */
export function NoteRailSkeleton({ cardWidth }: { cardWidth: number }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="flex animate-pulse gap-3 overflow-hidden px-5 pb-3 pt-1"
    >
      {[0, 1, 2].map((i) => (
        <div key={i} className="shrink-0" style={{ width: cardWidth }}>
          <div className="relative h-4">
            <span
              className="absolute left-2 top-2 h-0.5 -translate-y-1/2 bg-border"
              style={{ right: -12 }}
            />
            <Bone className="absolute left-0 top-0 h-4 w-4 rounded-full" />
          </div>
          <div className="space-y-2.5 pr-3 pt-4">
            <Bone className="h-5 w-3/4" />
            <Bone className="h-3.5 w-28" />
            <Bone className="h-3.5 w-36" />
            <Bone className="h-3.5 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
